import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ListPaginationBar from "../components/ListPaginationBar";
import DateTimeFilterField from "../components/search/DateTimeFilterField";
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from "../constants/pagination";
import { useHasPermission, useRequirePermission } from "../hooks/useHasPermission";
import { actionTypesAPI, reportsAPI } from "../services/api";
import { formatDate } from "../utils/dateFormatter";
import { PERMISSIONS } from "../utils/permissions";
import { buildActionsFiltersSummary } from "../utils/reportFilters";

const emptyFilters = () => ({
  ticketFrom: "",
  ticketTo: "",
  actionFrom: "",
  actionTo: "",
  actionTypes: [],
});

const ActionsReportPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const hasAccess = useRequirePermission(PERMISSIONS.VIEW_ACTIONS_REPORT, onNavigate);
  const canExport = useHasPermission(PERMISSIONS.EXPORT_ACTIONS_REPORT);

  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [actionTypes, setActionTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const typeName = (row) =>
    isRTL ? row.action_type_name_ar : row.action_type_name_en || row.action_type_name_ar;
  const employeeName = (row) =>
    isRTL ? row.employee_name_ar : row.employee_name_en || row.employee_name_ar;
  const branchLabel = (row) =>
    (row.branches || [])
      .map((item) =>
        isRTL ? item.name_ar || item.name_en : item.name_en || item.name_ar
      )
      .filter(Boolean)
      .join(" - ");

  const buildParams = useCallback(
    (source, withPage = true) => {
      const params = { lang: i18n.language };
      if (withPage) {
        params.page = page;
        params.page_size = pageSize;
      }
      if (source.ticketFrom) params.ticket_from = source.ticketFrom;
      if (source.ticketTo) params.ticket_to = source.ticketTo;
      if (source.actionFrom) params.action_from = source.actionFrom;
      if (source.actionTo) params.action_to = source.actionTo;
      if (source.actionTypes?.length) {
        params.action_types = source.actionTypes.join(",");
      }
      return params;
    },
    [i18n.language, page, pageSize]
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await reportsAPI.getActionsReport(buildParams(applied, true));
      const parsed = parsePaginatedResponse(response);
      setRows(parsed.items);
      setTotalCount(parsed.totalCount);
    } catch (err) {
      setError(err.message || t("actionsReport.loadError"));
    } finally {
      setLoading(false);
    }
  }, [applied, buildParams, t]);

  useEffect(() => {
    actionTypesAPI
      .list({ page_size: 200 })
      .then((response) => {
        const list = Array.isArray(response) ? response : response.results || [];
        setActionTypes(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      await reportsAPI.exportActionsReport(buildParams(applied, false));
    } catch (err) {
      setError(err.message || t("search.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const summary = useMemo(
    () => buildActionsFiltersSummary(applied, t, actionTypes, i18n.language),
    [applied, t, actionTypes, i18n.language]
  );

  if (!hasAccess) {
    return null;
  }

  return (
    <Box dir={isRTL ? "rtl" : "ltr"} sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: "100%" }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            {t("sidebar.actionsReport")}
          </Typography>
          {canExport && (
            <Button variant="outlined" onClick={handleExport} disabled={exporting}>
              {exporting ? <CircularProgress size={20} /> : t("search.exportExcel")}
            </Button>
          )}
        </Box>

        {summary.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary.map((line) => `${line.label} : ${line.value}`).join(" ، ")}
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            mb: 2,
          }}
        >
          <DateTimeFilterField
            label={t("actionsReport.ticketFrom")}
            value={filters.ticketFrom}
            onChange={(value) => setFilters((prev) => ({ ...prev, ticketFrom: value }))}
          />
          <DateTimeFilterField
            label={t("actionsReport.ticketTo")}
            value={filters.ticketTo}
            onChange={(value) => setFilters((prev) => ({ ...prev, ticketTo: value }))}
          />
          <DateTimeFilterField
            label={t("actionsReport.actionFrom")}
            value={filters.actionFrom}
            onChange={(value) => setFilters((prev) => ({ ...prev, actionFrom: value }))}
          />
          <DateTimeFilterField
            label={t("actionsReport.actionTo")}
            value={filters.actionTo}
            onChange={(value) => setFilters((prev) => ({ ...prev, actionTo: value }))}
          />
        </Box>

        <Autocomplete
          multiple
          options={actionTypes}
          value={actionTypes.filter((item) =>
            filters.actionTypes.includes(item.action_type_id)
          )}
          onChange={(_, value) =>
            setFilters((prev) => ({
              ...prev,
              actionTypes: value.map((item) => item.action_type_id),
            }))
          }
          getOptionLabel={(item) =>
            (isRTL ? item.name_ar : item.name_en) || item.name_ar || ""
          }
          isOptionEqualToValue={(opt, val) => opt.action_type_id === val.action_type_id}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.action_type_id}
                label={(isRTL ? option.name_ar : option.name_en) || option.name_ar}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label={t("actionsReport.actionTypes")} />
          )}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setPage(1);
              setApplied(filters);
            }}
          >
            {t("reports.apply")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              const cleared = emptyFilters();
              setFilters(cleared);
              setApplied(cleared);
              setPage(1);
            }}
          >
            {t("search.clearButton")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.actionId")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.ticketId")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.ticketTitle")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.branch")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.recordedBy")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.employeeNo")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.employeeName")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.action")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.date")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("actionsReport.note")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      {t("reports.noData")}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.action_id} hover>
                      <TableCell>{row.action_id}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          onClick={() => onNavigate(`/tickets/${row.ticket}`)}
                        >
                          {row.ticket}
                        </Link>
                      </TableCell>
                      <TableCell>{row.ticket_title}</TableCell>
                      <TableCell>{branchLabel(row) || "—"}</TableCell>
                      <TableCell>{row.created_by_name}</TableCell>
                      <TableCell>{row.employee_no || "—"}</TableCell>
                      <TableCell>{employeeName(row) || "—"}</TableCell>
                      <TableCell>{typeName(row)}</TableCell>
                      <TableCell>{formatDate(row.action_date)}</TableCell>
                      <TableCell>{row.note || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && (
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            isRTL={isRTL}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ActionsReportPage;
